import { createPortal } from 'react-dom';
import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import './SearchableSelect.css';
import { fold } from '../../utils/zoek';

export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Selecteer...',
  searchPlaceholder = 'Zoeken...',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const found = options.find((o) => o.value === value);
    return found ? found.label : null;
  }, [options, value]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = fold(search).trim();
    return options.filter((o) => fold(o.label).includes(q));
  }, [options, search]);

  // Plek bepalen: onder de knop, of erboven als er onderin te weinig ruimte is (telefoon).
  // De lijst wordt nooit hoger dan de zichtbare ruimte, dus alle opties blijven bereikbaar.
  const computePos = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vv = window.visualViewport;
    const vh = vv ? vv.height + vv.offsetTop : window.innerHeight;
    const below = vh - rect.bottom - 12;
    const above = rect.top - 12;
    const SEARCH_H = 52;
    const want = 220 + SEARCH_H;
    if (below < want && above > below) {
      setPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width, maxList: Math.max(120, Math.min(320, above - SEARCH_H)) });
    } else {
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width, maxList: Math.max(120, Math.min(320, below - SEARCH_H)) });
    }
  };

  // Compute dropdown position when opened
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    computePos();
  }, [open]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    const touch = window.matchMedia?.('(pointer: coarse)').matches;
    if (open && searchRef.current && !touch) {
      searchRef.current.focus();
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        !e.target.closest('.ss-dropdown-portal')
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Close and recompute on scroll/resize
  useEffect(() => {
    if (!open) return;
    const handler = () => {
      computePos();
    };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    window.visualViewport?.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
      window.visualViewport?.removeEventListener('resize', handler);
    };
  }, [open]);

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => {
      if (prev) setSearch('');
      return !prev;
    });
  };

  const handleSelect = (optValue) => {
    setOpen(false);
    setSearch('');
    if (onChange) onChange(optValue);
  };

  return (
    <div className="ss-wrap" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`ss-trigger${open ? ' ss-open' : ''}`}
        onClick={handleTriggerClick}
        disabled={disabled}
      >
        <span className={`ss-trigger-label${!selectedLabel ? ' ss-placeholder' : ''}`}>
          {selectedLabel || placeholder}
        </span>
        <span className="ss-trigger-arrow">
          <ChevronDown size={14} />
        </span>
      </button>

      {open && createPortal(
        <div
          className="ss-dropdown ss-dropdown-portal"
          style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, minWidth: pos.width, maxWidth: 'calc(100vw - 16px)' }}
        >
          <div className="ss-search-wrap">
            <input
              ref={searchRef}
              className="ss-search"
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ss-options" style={pos.maxList ? { maxHeight: pos.maxList } : undefined}>
            {filtered.length === 0 ? (
              <div className="ss-no-results">Geen resultaten</div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  className={`ss-option${o.value === value ? ' ss-selected' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(o.value)}
                >
                  <span className="ss-option-label">{o.label}</span>
                  {o.badge && <span className="ss-option-badge">{o.badge}</span>}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
